// pages/ReportesFacturacion.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { obtenerFacturas } from '../services/facturaService';
import api from '../services/api';
import { formatearFecha } from '../utils/dateUtils';
import '../styles/ReportesFacturacion.css';
import * as XLSX from 'xlsx';
import { useFiscal } from '../hooks/useFiscal';

const ReportesFacturacion = () => {
  const navigate = useNavigate();
  const fiscal = useFiscal();
  const [tipoReporte, setTipoReporte] = useState('periodo');
  const [loading, setLoading] = useState(false);
  const [reporte, setReporte] = useState(null);

  // Filtros
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  
  const [clientes, setClientes] = useState([]);
  const [proyectos, setProyectos] = useState([]);

  useEffect(() => {
    cargarDatos();
    inicializarFechas();
  }, []);

  const inicializarFechas = () => {
    const hoy = new Date();
    const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    setFechaInicio(primerDiaMes.toISOString().split('T')[0]);
    setFechaFin(hoy.toISOString().split('T')[0]);
  };

  const cargarDatos = async () => {
    try {
      const [resClientes, resProyectos] = await Promise.all([
        api.get('/clientes'),
        api.get('/proyectos')
      ]);
      
      const clientesData = Array.isArray(resClientes.data) 
        ? resClientes.data 
        : resClientes.data.clientes || [];
      const proyectosData = Array.isArray(resProyectos.data)
        ? resProyectos.data
        : resProyectos.data.proyectos || [];
      
      setClientes(clientesData);
      setProyectos(proyectosData);
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  const generarReporte = async () => {
    try {
      setLoading(true);
      const facturas = await obtenerFacturas({});
      
      let facturasFiltradas = facturas;

      // Aplicar filtros según tipo de reporte
      switch (tipoReporte) {
        case 'periodo':
          facturasFiltradas = filtrarPorPeriodo(facturas);
          break;
        case 'cliente':
          if (!clienteId) {
            alert('Selecciona un cliente');
            setLoading(false);
            return;
          }
          facturasFiltradas = filtrarPorCliente(facturas);
          break;
        case 'proyecto':
          if (!proyectoId) {
            alert('Selecciona un proyecto');
            setLoading(false);
            return;
          }
          facturasFiltradas = filtrarPorProyecto(facturas);
          break;
        case 'estado-cuenta':
          if (!clienteId) {
            alert('Selecciona un cliente');
            setLoading(false);
            return;
          }
          facturasFiltradas = filtrarPorCliente(facturas);
          break;
        default:
          break;
      }

      const datosReporte = calcularDatosReporte(facturasFiltradas);
      setReporte(datosReporte);
      
    } catch (error) {
      console.error('Error generando reporte:', error);
      alert('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const filtrarPorPeriodo = (facturas) => {
    return facturas.filter(f => {
      const fecha = new Date(f.fecha_emision);
      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      return fecha >= inicio && fecha <= fin;
    });
  };

  const filtrarPorCliente = (facturas) => {
    return facturas.filter(f => f.ClienteId === parseInt(clienteId));
  };

  const filtrarPorProyecto = (facturas) => {
    return facturas.filter(f => f.ProyectoId === parseInt(proyectoId));
  };

  const calcularDatosReporte = (facturas) => {
    let totalFacturado = 0;
    let totalCobrado = 0;
    let totalPendiente = 0;
    let totalVencido = 0;
    
    const facturasPorEstado = {
      borrador: 0,
      emitida: 0,
      parcial: 0,
      pagada: 0,
      vencida: 0,
      anulada: 0
    };

    facturas.forEach(f => {
      totalFacturado += parseFloat(f.total || 0);
      totalCobrado += parseFloat(f.totalPagado || 0);
      totalPendiente += parseFloat(f.saldoPendiente || 0);
      
      if (f.estado === 'vencida') {
        totalVencido += parseFloat(f.saldoPendiente || 0);
      }
      
      facturasPorEstado[f.estado] = (facturasPorEstado[f.estado] || 0) + 1;
    });

    return {
      facturas,
      totalFacturado,
      totalCobrado,
      totalPendiente,
      totalVencido,
      facturasPorEstado,
      cantidadFacturas: facturas.length
    };
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: fiscal.moneda?.codigo || 'DOP'
    }).format(monto);
  };

const exportarExcel = async () => {
  if (!reporte) {
    alert('Genera un reporte primero');
    return;
  }

  // Importar ExcelJS dinámicamente
  const ExcelJS = (await import('exceljs')).default;
  
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Reporte Facturación');

  // ENCABEZADO PRINCIPAL
  worksheet.mergeCells('A1:H1');
  const titleCell = worksheet.getCell('A1');
  titleCell.value = 'REPORTE DE FACTURACIÓN';
  titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF0066CC' }
  };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.getRow(1).height = 30;

  // SUBTÍTULO
  worksheet.mergeCells('A2:H2');
  const subtitleCell = worksheet.getCell('A2');
  if (tipoReporte === 'periodo') {
    subtitleCell.value = `Período: ${formatearFecha(fechaInicio)} - ${formatearFecha(fechaFin)}`;
  } else if (tipoReporte === 'cliente') {
    subtitleCell.value = `Cliente: ${getClienteNombre(clienteId)}`;
  } else if (tipoReporte === 'proyecto') {
    subtitleCell.value = `Proyecto: ${getProyectoNombre(proyectoId)}`;
  } else {
    subtitleCell.value = `Estado de Cuenta - ${getClienteNombre(clienteId)}`;
  }
  subtitleCell.font = { bold: true, size: 12 };
  subtitleCell.alignment = { horizontal: 'center' };

  // FECHA DE GENERACIÓN
  worksheet.mergeCells('A3:H3');
  const dateCell = worksheet.getCell('A3');
  dateCell.value = `Generado: ${new Date().toLocaleString('es-DO')}`;
  dateCell.font = { size: 10, italic: true };
  dateCell.alignment = { horizontal: 'center' };

  // RESUMEN EJECUTIVO
  let currentRow = 5;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  const resumenTitle = worksheet.getCell(`A${currentRow}`);
  resumenTitle.value = 'RESUMEN EJECUTIVO';
  resumenTitle.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  resumenTitle.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF28A745' }
  };
  resumenTitle.alignment = { horizontal: 'center' };

  currentRow++;
  const resumenData = [
    ['Total Facturado', reporte.totalFacturado],
    ['Total Cobrado', reporte.totalCobrado],
    ['Pendiente de Cobro', reporte.totalPendiente],
    ['Vencido', reporte.totalVencido]
  ];

  resumenData.forEach((row, idx) => {
    worksheet.getCell(`A${currentRow}`).value = row[0];
    worksheet.getCell(`B${currentRow}`).value = row[1];
    worksheet.getCell(`B${currentRow}`).numFmt = '"RD$"#,##0.00';
    
    // Estilos alternados
    const fillColor = idx % 2 === 0 ? 'FFF8F9FA' : 'FFFFFFFF';
    worksheet.getCell(`A${currentRow}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: fillColor }
    };
    worksheet.getCell(`B${currentRow}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: fillColor }
    };
    
    currentRow++;
  });

  // DETALLE DE FACTURAS
  currentRow += 2;
  worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
  const detalleTitle = worksheet.getCell(`A${currentRow}`);
  detalleTitle.value = 'DETALLE DE FACTURAS';
  detalleTitle.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  detalleTitle.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF0066CC' }
  };
  detalleTitle.alignment = { horizontal: 'center' };

  // HEADERS DE TABLA
  const headerRow = worksheet.getRow(currentRow);
headerRow.values = ['Número', 'Fecha Emisión', 'Cliente', 'Proyecto', 'Total', 'Cobrado', 'Pendiente', 'Estado'];
headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
headerRow.height = 25;

// Aplicar el fill solo a las 8 columnas
for (let col = 1; col <= 8; col++) {
  headerRow.getCell(col).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF495057' }
  };
}
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
  headerRow.height = 25;

  // DATOS DE FACTURAS
  currentRow++;
  reporte.facturas.forEach((factura, idx) => {
    const row = worksheet.getRow(currentRow);
    row.values = [
      factura.numero_factura,
      formatearFecha(factura.fecha_emision),
      factura.cliente?.nombre || '',
      factura.proyecto?.nombre || '',
      parseFloat(factura.total),
      parseFloat(factura.totalPagado || 0),
      parseFloat(factura.saldoPendiente || 0),
      factura.estado.toUpperCase()
    ];

    // Formato de moneda
    row.getCell(5).numFmt = '"RD$"#,##0.00';
    row.getCell(6).numFmt = '"RD$"#,##0.00';
    row.getCell(7).numFmt = '"RD$"#,##0.00';

    // Colores alternados
    const fillColor = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF8F9FA';
    row.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: fillColor }
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } }
      };
    });

    currentRow++;
  });

 // FILA DE TOTALES
const totalesRow = worksheet.getRow(currentRow);
totalesRow.values = ['', '', '', 'TOTALES:', reporte.totalFacturado, reporte.totalCobrado, reporte.totalPendiente, ''];
totalesRow.font = { bold: true };
totalesRow.getCell(5).numFmt = '"RD$"#,##0.00';
totalesRow.getCell(6).numFmt = '"RD$"#,##0.00';
totalesRow.getCell(7).numFmt = '"RD$"#,##0.00';

// Aplicar fill y bordes solo a las 8 columnas
for (let col = 1; col <= 8; col++) {
  const cell = totalesRow.getCell(col);
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFDE047' }
  };
  cell.border = {
    top: { style: 'medium', color: { argb: 'FF000000' } },
    bottom: { style: 'medium', color: { argb: 'FF000000' } }
  };
}

  // AJUSTAR ANCHOS DE COLUMNA
  worksheet.columns = [
    { width: 15 }, // Número
    { width: 15 }, // Fecha
    { width: 30 }, // Cliente
    { width: 30 }, // Proyecto
    { width: 15 }, // Total
    { width: 15 }, // Cobrado
    { width: 15 }, // Pendiente
    { width: 12 }  // Estado
  ];

  // GENERAR ARCHIVO
  let nombreArchivo = 'Reporte_Facturacion_';
  if (tipoReporte === 'periodo') {
    nombreArchivo += `${fechaInicio}_${fechaFin}`;
  } else if (tipoReporte === 'cliente') {
    const clienteNombre = getClienteNombre(clienteId).replace(/\s+/g, '_');
    nombreArchivo += `Cliente_${clienteNombre}`;
  } else if (tipoReporte === 'proyecto') {
    const proyectoNombre = getProyectoNombre(proyectoId).replace(/\s+/g, '_');
    nombreArchivo += `Proyecto_${proyectoNombre}`;
  } else {
    const clienteNombre = getClienteNombre(clienteId).replace(/\s+/g, '_');
    nombreArchivo += `Estado_Cuenta_${clienteNombre}`;
  }
  nombreArchivo += '.xlsx';

  // DESCARGAR
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  a.click();
  window.URL.revokeObjectURL(url);
};
  const imprimirReporte = () => {
    window.print();
  };

  const getClienteNombre = (id) => {
    const cliente = clientes.find(c => c.id === parseInt(id));
    return cliente?.nombre || '';
  };

  const getProyectoNombre = (id) => {
    const proyecto = proyectos.find(p => p.id === parseInt(id));
    return proyecto?.nombre || '';
  };

  return (
    <div className="reportes-facturacion-container" id="reportArea">
      {/* Header */}
      <div className="reportes-header no-print">
        <div>
          <h1>📑 Reportes de Facturación</h1>
          <p className="subtitle">Análisis y reportes detallados</p>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/facturas')}
        >
          ← Volver a Facturas
        </button>
      </div>

      {/* Pestañas de tipo de reporte */}
      <div className="tabs-container no-print">
        <button
          className={`tab ${tipoReporte === 'periodo' ? 'active' : ''}`}
          onClick={() => setTipoReporte('periodo')}
        >
          📅 Por Período
        </button>
        <button
          className={`tab ${tipoReporte === 'cliente' ? 'active' : ''}`}
          onClick={() => setTipoReporte('cliente')}
        >
          👤 Por Cliente
        </button>
        <button
          className={`tab ${tipoReporte === 'proyecto' ? 'active' : ''}`}
          onClick={() => setTipoReporte('proyecto')}
        >
          🏗️ Por Proyecto
        </button>
        <button
          className={`tab ${tipoReporte === 'estado-cuenta' ? 'active' : ''}`}
          onClick={() => setTipoReporte('estado-cuenta')}
        >
          💰 Estado de Cuenta
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros-card no-print">
        <h3>Configuración del Reporte</h3>
        
        <div className="filtros-grid">
          {tipoReporte === 'periodo' && (
            <>
              <div className="form-group">
                <label>Fecha Inicio *</label>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Fecha Fin *</label>
                <input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  className="form-control"
                />
              </div>
            </>
          )}

          {(tipoReporte === 'cliente' || tipoReporte === 'estado-cuenta') && (
            <div className="form-group">
              <label>Cliente *</label>
              <select
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                className="form-control"
              >
                <option value="">Selecciona un cliente</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {tipoReporte === 'proyecto' && (
            <div className="form-group">
              <label>Proyecto *</label>
              <select
                value={proyectoId}
                onChange={(e) => setProyectoId(e.target.value)}
                className="form-control"
              >
                <option value="">Selecciona un proyecto</option>
                {proyectos.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <button
          className="btn btn-primary"
          onClick={generarReporte}
          disabled={loading}
        >
          {loading ? 'Generando...' : '📊 Generar Reporte'}
        </button>
      </div>

      {/* Resultados */}
      {reporte && (
        <>
          {/* Acciones */}
          <div className="acciones-reporte no-print">
            <button className="btn btn-success" onClick={exportarExcel}>
              📥 Exportar a Excel
            </button>
            <button className="btn btn-secondary" onClick={imprimirReporte}>
              🖨️ Imprimir
            </button>
          </div>

          {/* Resumen */}
          <div className="reporte-header print-only">
            <h2>Reporte de Facturación</h2>
            <p>
              {tipoReporte === 'periodo' && `Período: ${formatearFecha(fechaInicio)} - ${formatearFecha(fechaFin)}`}
              {tipoReporte === 'cliente' && `Cliente: ${getClienteNombre(clienteId)}`}
              {tipoReporte === 'proyecto' && `Proyecto: ${getProyectoNombre(proyectoId)}`}
              {tipoReporte === 'estado-cuenta' && `Estado de Cuenta - ${getClienteNombre(clienteId)}`}
            </p>
            <p className="fecha-generacion">Generado: {new Date().toLocaleString('es-DO')}</p>
          </div>

          <div className="resumen-grid">
            <div className="resumen-card primary">
              <div className="resumen-label">Total Facturado</div>
              <div className="resumen-value">{formatearMonto(reporte.totalFacturado)}</div>
             
            </div>

            <div className="resumen-card success">
              <div className="resumen-label">Total Cobrado</div>
              <div className="resumen-value">{formatearMonto(reporte.totalCobrado)}</div>
            
            </div>

            <div className="resumen-card warning">
              <div className="resumen-label">Pendiente de Cobro</div>
              <div className="resumen-value">{formatearMonto(reporte.totalPendiente)}</div>
             
            </div>

            <div className="resumen-card danger">
              <div className="resumen-label">Vencido</div>
              <div className="resumen-value">{formatearMonto(reporte.totalVencido)}</div>
            </div>
          </div>

          {/* Tabla de facturas */}
          <div className="tabla-reporte">
            <h3>Detalle de Facturas</h3>
            <table className="reporte-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Proyecto</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Cobrado</th>
                  <th className="text-right">Pendiente</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {reporte.facturas.map(factura => (
                  <tr key={factura.id}>
                    <td className="font-bold">{factura.numero_factura}</td>
                    <td>{formatearFecha(factura.fecha_emision)}</td>
                    <td>{factura.cliente?.nombre}</td>
                    <td>{factura.proyecto?.nombre}</td>
                    <td className="text-right">{formatearMonto(factura.total)}</td>
                    <td className="text-right text-success">{formatearMonto(factura.totalPagado || 0)}</td>
                    <td className="text-right text-warning">{formatearMonto(factura.saldoPendiente || 0)}</td>
                    <td>
                      <span className={`badge badge-${factura.estado}`}>
                        {factura.estado.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totales-row">
                  <td colSpan="4" className="text-right font-bold">TOTALES:</td>
                  <td className="text-right font-bold">{formatearMonto(reporte.totalFacturado)}</td>
                  <td className="text-right font-bold text-success">{formatearMonto(reporte.totalCobrado)}</td>
                  <td className="text-right font-bold text-warning">{formatearMonto(reporte.totalPendiente)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Estado de cuenta detallado */}
          {tipoReporte === 'estado-cuenta' && (
            <div className="estado-cuenta-detalle">
              <h3>Resumen de Saldos</h3>
              <div className="saldos-grid">
                <div className="saldo-item">
                  <span>Total Facturado:</span>
                  <span className="font-bold">{formatearMonto(reporte.totalFacturado)}</span>
                </div>
                <div className="saldo-item success">
                  <span>Total Pagado:</span>
                  <span className="font-bold">{formatearMonto(reporte.totalCobrado)}</span>
                </div>
                <div className="saldo-item warning">
                  <span>Saldo Pendiente:</span>
                  <span className="font-bold">{formatearMonto(reporte.totalPendiente)}</span>
                </div>
                {reporte.totalVencido > 0 && (
                  <div className="saldo-item danger">
                    <span>Saldo Vencido:</span>
                    <span className="font-bold">{formatearMonto(reporte.totalVencido)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!reporte && !loading && (
        <div className="empty-state">
          <p className="empty-icon">📊</p>
          <h3>Selecciona los filtros y genera un reporte</h3>
          <p>Configura los parámetros y haz click en "Generar Reporte"</p>
        </div>
      )}
    </div>
  );
};

const printStyles = `
  @media print {
    @page {
      margin: 10mm;
      size: letter;
    }
    
    body {
      margin: 0;
      padding: 0;
    }
    
    body * {
      visibility: hidden !important;
    }
    
    #reportArea, #reportArea * {
      visibility: visible !important;
    }
    
    #reportArea {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      background: white !important;
      padding: 0;
    }
    
    .no-print {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    /* Header compacto */
    .reporte-header.print-only {
      text-align: center;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 2px solid #dee2e6;
    }
    
    .reporte-header.print-only h2 {
      font-size: 18px;
      margin: 0 0 5px 0;
    }
    
    .reporte-header.print-only p {
      margin: 3px 0;
      font-size: 11px;
    }
    
    .fecha-generacion {
      font-size: 10px;
      color: #6c757d;
    }
    
    /* Tarjetas de resumen más compactas */
    .resumen-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      margin-bottom: 15px;
      page-break-inside: avoid;
    }
    
    .resumen-card {
      padding: 10px;
      box-shadow: none;
      border: 1px solid #dee2e6;
    }
    
    .resumen-label {
      font-size: 9px;
      margin-bottom: 3px;
    }
    
    .resumen-value {
      font-size: 14px;
      margin: 0;
    }
    
    /* Tabla compacta */
    .tabla-reporte {
      box-shadow: none;
      padding: 0;
      margin-bottom: 0;
    }
    
    .tabla-reporte h3 {
      font-size: 14px;
      margin: 0 0 10px 0;
    }
    
    .reporte-table {
      font-size: 9px;
    }
    
    .reporte-table th {
      padding: 5px 4px;
      font-size: 9px;
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .reporte-table td {
      padding: 4px;
      font-size: 9px;
    }
    
    .reporte-table tfoot tr {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .totales-row td {
      padding: 6px 4px;
      font-size: 10px;
    }
    
    /* Badges compactos */
    .badge {
      padding: 2px 6px;
      font-size: 8px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    /* Estado de cuenta */
    .estado-cuenta-detalle {
      box-shadow: none;
      padding: 10px;
      margin-bottom: 10px;
      page-break-inside: avoid;
    }
    
    .estado-cuenta-detalle h3 {
      font-size: 12px;
      margin: 0 0 8px 0;
    }
    
    .saldos-grid {
      gap: 8px;
    }
    
    .saldo-item {
      padding: 8px;
      font-size: 10px;
    }
    
    /* Forzar todo en una página */
    .resumen-grid,
    .tabla-reporte,
    .estado-cuenta-detalle {
      page-break-inside: avoid;
    }
  }
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = printStyles;
  document.head.appendChild(styleSheet);
}

export default ReportesFacturacion;